import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Camera, X, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import { api } from "@/lib/api";
import PawSpinner from "@/components/PawSpinner";
import GeneratingLoader from "@/components/GeneratingLoader";

export default function CreateCharacter() {
  const { userId } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  const handlePhotoAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 10) {
      toast.error("Maximum 10 photos allowed");
      return;
    }
    setUploading(true);
    for (const file of files) {
      try {
        const preview = URL.createObjectURL(file);
        const { data } = await api.uploadFile(file, userId);
        setPhotos((prev) => [...prev, { preview, storagePath: data.path }]);
      } catch {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removePhoto = (index) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGeneratePreview = async () => {
    if (photos.length < 3) {
      toast.error("Please upload at least 3 photos");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.generateCharacterPreview({
        reference_photos: photos.map((p) => p.storagePath),
        character_name: name,
      });
      setPreviewUrl(data.generated_preview_url);
      toast.success("Preview generated!");
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Generation didn't work this time. Try again?"
      );
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: created } = await api.createCharacter({
        user_id: userId,
        name,
        reference_photos: photos.map((p) => p.storagePath),
      });
      if (previewUrl) {
        await api.updateCharacter(created.id, {
          generated_preview_url: previewUrl,
        });
      }
      queryClient.invalidateQueries({ queryKey: ["characters"] });
      toast.success(`${name} has been saved!`);
      navigate("/characters");
    } catch {
      toast.error("Failed to save character");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="space-y-6 py-2 max-w-lg mx-auto"
      data-testid="create-character-screen"
    >
      <div>
        <button
          onClick={() => navigate(-1)}
          className="text-text-secondary text-sm mb-2 flex items-center gap-1 hover:text-text-primary transition-colors"
          data-testid="char-back-btn"
        >
          <ChevronLeft size={16} /> Back
        </button>
        <h1 className="font-heading text-2xl text-text-primary">
          New Character
        </h1>
        <div className="flex items-center gap-4 mt-3">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-blush" : "bg-border-pink"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Step 1: Name */}
      {step === 1 && (
        <div className="space-y-4" data-testid="char-step-1">
          <h2 className="font-heading text-lg text-text-primary">
            Name your character
          </h2>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Waimbow, Grandma Rose"
            className="w-full px-4 py-3 rounded-input border border-border-pink bg-white text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:ring-2 focus:ring-blush/30 text-sm"
            data-testid="character-name-input"
            onKeyDown={(e) => e.key === "Enter" && name.trim() && setStep(2)}
            autoFocus
          />
          <button
            onClick={() => setStep(2)}
            disabled={!name.trim()}
            className="w-full bg-blush hover:bg-blush-dark disabled:opacity-50 text-white py-3 rounded-pill text-sm font-medium transition-colors flex items-center justify-center gap-2"
            data-testid="char-step-1-next"
          >
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}

      {/* Step 2: Upload photos */}
      {step === 2 && (
        <div className="space-y-4" data-testid="char-step-2">
          <h2 className="font-heading text-lg text-text-primary">
            Upload reference photos
          </h2>
          <p className="text-sm text-text-secondary">
            The more photos, the better Gemini can recognise them. Minimum 3,
            maximum 10.
          </p>

          <div className="grid grid-cols-3 gap-3">
            {photos.map((photo, i) => (
              <div
                key={photo.storagePath}
                className="relative aspect-square rounded-input overflow-hidden"
              >
                <img
                  src={photo.preview}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center hover:bg-black/70 transition-colors"
                  data-testid={`remove-photo-${i}`}
                >
                  <X size={12} className="text-white" />
                </button>
              </div>
            ))}
            {photos.length < 10 && (
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                className="aspect-square rounded-input border-2 border-dashed border-border-pink flex flex-col items-center justify-center gap-1 cursor-pointer hover:bg-cream/50 transition-colors"
                data-testid="add-photo-btn"
              >
                {uploading ? (
                  <PawSpinner size="sm" />
                ) : (
                  <>
                    <Camera size={20} className="text-blush" />
                    <span className="text-[10px] text-text-secondary">Add</span>
                  </>
                )}
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handlePhotoAdd}
            className="hidden"
            data-testid="char-photo-input"
          />
          <p className="text-xs text-text-secondary">
            {photos.length}/10 photos uploaded
          </p>

          <div className="flex gap-3">
            <button
              onClick={() => setStep(1)}
              className="flex-1 py-3 rounded-pill text-sm font-medium border border-border-pink text-text-secondary hover:bg-cream transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => setStep(3)}
              disabled={photos.length < 3}
              className="flex-1 bg-blush hover:bg-blush-dark disabled:opacity-50 text-white py-3 rounded-pill text-sm font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="char-step-2-next"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Generate preview */}
      {step === 3 && (
        <div className="space-y-4" data-testid="char-step-3">
          <h2 className="font-heading text-lg text-text-primary">
            Generate character preview
          </h2>

          {!previewUrl && !generating && (
            <button
              onClick={handleGeneratePreview}
              disabled={generating}
              className="w-full bg-gold hover:bg-gold/80 disabled:opacity-50 text-white py-3 rounded-pill text-sm font-medium transition-colors flex items-center justify-center gap-2"
              data-testid="generate-preview-btn"
            >
              <Sparkles size={16} /> Generate character preview
            </button>
          )}

          {generating && (
            <GeneratingLoader message={`Bringing ${name} to life...`} />
          )}

          {previewUrl && (
            <div className="space-y-3">
              <img
                src={api.getFileUrl(previewUrl)}
                alt={name}
                className="w-full rounded-card shadow-soft"
                data-testid="character-preview-img"
              />
              <div className="flex gap-3">
                <button
                  onClick={handleGeneratePreview}
                  disabled={generating}
                  className="flex-1 py-2.5 rounded-pill text-sm font-medium border border-border-pink text-text-secondary hover:bg-cream transition-colors"
                  data-testid="try-again-btn"
                >
                  {generating ? "Generating..." : "Try again"}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-blush hover:bg-blush-dark disabled:opacity-50 text-white py-2.5 rounded-pill text-sm font-medium transition-colors"
                  data-testid="save-character-btn"
                >
                  {saving ? "Saving..." : "Looks good, save"}
                </button>
              </div>
            </div>
          )}

          {!previewUrl && (
            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-3 rounded-pill text-sm font-medium border border-border-pink text-text-secondary hover:bg-cream transition-colors"
              >
                Back
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-blush/80 hover:bg-blush-dark text-white py-3 rounded-pill text-sm font-medium transition-colors"
                data-testid="skip-save-character-btn"
              >
                {saving ? "Saving..." : "Skip & save character"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
